import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Shield, Award, Clock, Star } from 'lucide-react';

// DSGVO-konforme Platzhalter-Hintergründe (keine externen Bilder)
const heroBackgrounds = [
  'linear-gradient(135deg, #1a1a1a 0%, #2d1f1f 50%, #1a1a1a 100%)',
  'linear-gradient(135deg, #1f1a1a 0%, #2a1a1a 50%, #1a1a1a 100%)',
  'linear-gradient(135deg, #1a1a1f 0%, #1f2020 50%, #1a1a1a 100%)',
  'linear-gradient(135deg, #201a1a 0%, #251a1a 50%, #1a1a1a 100%)'
];

export const HeroSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroBackgrounds.length);
    }, 7000);
    return () => clearInterval(timer);
  }, []);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % heroBackgrounds.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + heroBackgrounds.length) % heroBackgrounds.length);
  };

  return (
    <section id="hero" className="relative min-h-screen overflow-hidden">
      {/* Animated Background Slider */}
      <div className="absolute inset-0">
        {heroBackgrounds.map((background, index) => (
          <div
            key={index}
            className="absolute inset-0"
            style={{
              opacity: index === currentSlide ? 1 : 0,
              transition: 'opacity 1.5s ease-in-out',
              zIndex: index === currentSlide ? 1 : 0
            }}
          >
            <div
              className="absolute inset-0"
              style={{ background: background }}
            />
            <div className="absolute inset-0 bg-gradient-to-br from-black/80 via-black/70 to-red-900/60"></div>
            <div className="absolute inset-0 opacity-20 mix-blend-overlay" style={{
              backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\'/%3E%3C/svg%3E")'
            }}></div>
          </div>
        ))}
      </div>

      {/* Sparkle Particles - reduced for mobile */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-10 hidden sm:block">
        {[...Array(10)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-red-500 rounded-full"
            initial={{
              x: Math.random() * (typeof window !== 'undefined' ? window.innerWidth : 1920),
              y: Math.random() * (typeof window !== 'undefined' ? window.innerHeight : 1080),
              opacity: 0
            }}
            animate={{
              y: [null, Math.random() * (typeof window !== 'undefined' ? window.innerHeight : 1080)],
              opacity: [0, 1, 0]
            }}
            transition={{
              duration: 3 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2
            }}
          />
        ))}
      </div>

      {/* Content */}
      <div className="relative z-20 min-h-screen flex items-center pt-20 pb-24 sm:pt-0 sm:pb-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-center"
          >
            {/* Hero Headline - Mobile optimized */}
            <motion.h1
              className="text-3xl sm:text-5xl md:text-7xl font-bold text-white mb-4 sm:mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              Ihr Dach in
              <span className="block bg-gradient-to-r from-red-500 via-red-600 to-red-800 bg-clip-text text-transparent mt-1 sm:mt-2">
                Meisterhänden
              </span>
            </motion.h1>

            <motion.p
              className="text-base sm:text-xl md:text-2xl text-gray-200 mb-6 sm:mb-10 max-w-3xl mx-auto px-2"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.5 }}
            >
              Professionelle Dachdeckerarbeiten und Fassadengestaltung seit über 10 Jahren
            </motion.p>

            {/* Trust Icons - Mobile: 2x2 Grid, Desktop: 4 columns */}
            <motion.div
              className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-6 sm:mb-10 max-w-4xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.7 }}
            >
              <TrustBadge icon={<Shield />} title="Sicher" description="Gelerntes Personal" />
              <TrustBadge icon={<Award />} title="Meisterbetrieb" description="Neuste Technik" />
              <TrustBadge icon={<Clock />} title="Pünktlich" description="Zuverlässig" />
              <TrustBadge icon={<Star />} title="Qualität" description="Top Material" />
            </motion.div>

            {/* CTA Button - Mobile optimized */}
            <motion.button
              onClick={() => document.getElementById('kontakt').scrollIntoView({ behavior: 'smooth' })}
              className="relative group px-8 sm:px-12 py-4 sm:py-5 bg-gradient-to-r from-red-600 to-red-800 text-white text-base sm:text-lg font-bold rounded-lg overflow-hidden shadow-lg"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.9 }}
            >
              <span className="relative z-10">Jetzt Beratung sichern</span>
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-red-700 to-red-900"
                initial={{ x: '-100%' }}
                whileHover={{ x: 0 }}
                transition={{ duration: 0.3 }}
              />
            </motion.button>
          </motion.div>
        </div>
      </div>

      {/* Slider Controls - Smaller on mobile */}
      <div className="absolute bottom-4 sm:bottom-8 left-0 right-0 z-30 flex justify-center items-center space-x-3 sm:space-x-4">
        <button
          onClick={prevSlide}
          className="p-2 sm:p-3 bg-white/10 backdrop-blur-md rounded-full hover:bg-white/20 transition-all"
          aria-label="Vorheriges Bild"
        >
          <ChevronLeft className="text-white" size={20} />
        </button>
        <div className="flex space-x-2">
          {heroBackgrounds.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`h-2 rounded-full transition-all ${
                index === currentSlide ? 'w-6 sm:w-8 bg-red-600' : 'w-2 bg-white/50'
              }`}
              aria-label={`Slide ${index + 1}`}
            />
          ))}
        </div>
        <button
          onClick={nextSlide}
          className="p-2 sm:p-3 bg-white/10 backdrop-blur-md rounded-full hover:bg-white/20 transition-all"
          aria-label="Nächstes Bild"
        >
          <ChevronRight className="text-white" size={20} />
        </button>
      </div>
    </section>
  );
};

// Mobile-optimized Trust Badge
const TrustBadge = ({ icon, title, description }) => (
  <motion.div
    className="flex flex-col items-center text-center text-white bg-white/10 backdrop-blur-lg rounded-lg sm:rounded-xl p-3 sm:p-5 border border-white/20"
    whileHover={{ scale: 1.02 }}
    transition={{ duration: 0.2 }}
  >
    <div className="p-2 sm:p-3 bg-gradient-to-br from-red-600 to-red-800 rounded-full mb-2 sm:mb-3 shadow-lg">
      {React.cloneElement(icon, { size: 20, className: 'text-white sm:w-6 sm:h-6' })}
    </div>
    <h3 className="text-sm sm:text-base font-bold mb-0.5 sm:mb-1">{title}</h3>
    <p className="text-xs sm:text-sm text-gray-300">{description}</p>
  </motion.div>
);
